import fs from 'node:fs';
import { isMainThread } from 'node:worker_threads';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { READ_RANGE_YIELD_EVERY_BYTES } from './types.js';

const READ_CHUNK_BYTES = 256 * 1024;
/** Interactive reads on the main thread bypass the libuv pool when under this size. */
const MAIN_THREAD_SYNC_READ_MAX_BYTES = 512 * 1024;

/** Read an entire file into a buffer, yielding so the bridge event loop stays responsive. */
export async function readFileBufferFullAsync(
  filePath: string,
  knownSize?: number,
): Promise<{ buffer: Buffer; size: number }> {
  if (isMainThread) {
    const size = knownSize ?? fs.statSync(filePath).size;
    if (size <= MAIN_THREAD_SYNC_READ_MAX_BYTES) {
      await yieldToEventLoop();
      const buffer = fs.readFileSync(filePath);
      return { buffer, size: buffer.length };
    }
  }

  const size = knownSize ?? (await fs.promises.stat(filePath)).size;
  const fd = await fs.promises.open(filePath, 'r');
  const chunks: Buffer[] = [];
  let position = 0;
  let bytesSinceYield = 0;

  try {
    while (position < size) {
      const buf = Buffer.alloc(Math.min(READ_CHUNK_BYTES, size - position));
      const { bytesRead } = await fd.read(buf, 0, buf.length, position);
      if (bytesRead === 0) break;
      chunks.push(buf.subarray(0, bytesRead));
      position += bytesRead;
      bytesSinceYield += bytesRead;
      if (bytesSinceYield >= READ_RANGE_YIELD_EVERY_BYTES) {
        await yieldToEventLoop();
        bytesSinceYield = 0;
      }
    }
  } finally {
    await fd.close();
  }

  return { buffer: Buffer.concat(chunks), size };
}
